import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Filter, Download, Eye, Edit2, Trash2, Plus, Building2, AlertCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const CompanyManagement = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewCompany, setViewCompany] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('Please log in as a Super Admin to manage companies.');
    }

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const getErrorMessage = async (response, fallbackMessage) => {
    try {
      const data = await response.json();
      return data?.detail || fallbackMessage;
    } catch (error) {
      return fallbackMessage;
    }
  };

  // Fetch companies from backend
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      if (!backendUrl) {
        throw new Error('Backend URL is not configured.');
      }

      const headers = getAuthHeaders();
      const response = await fetch(`${backendUrl}/api/companies`, { headers });
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to load companies.');
        throw new Error(message);
      }

      const companiesData = await response.json();
      const companiesWithCounts = await Promise.all(
        companiesData.map(async (company) => {
          const [branchesResponse, usersResponse] = await Promise.all([
            fetch(`${backendUrl}/api/branches?company_id=${company.id}`, { headers }),
            fetch(`${backendUrl}/api/users?company_id=${company.id}`, { headers })
          ]);

          let branchesCount = 0;
          let usersCount = 0;

          if (branchesResponse.ok) {
            const branches = await branchesResponse.json();
            branchesCount = Array.isArray(branches) ? branches.length : 0;
          }

          if (usersResponse.ok) {
            const users = await usersResponse.json();
            usersCount = Array.isArray(users) ? users.length : 0;
          }

          return {
            ...company,
            branches: branchesCount,
            totalUsers: usersCount,
            businessModes: company.business_modes || [],
            shipmentTypes: company.allowed_shipment_types || [],
            status: typeof company.status === 'string' ? company.status.toLowerCase() : 'active'
          };
        })
      );

      setCompanies(companiesWithCounts);
    } catch (error) {
      setCompanies([]);
      setErrorMessage(error.message || 'Failed to load companies.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Handle delete
  const handleDeleteClick = (company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setErrorMessage('');
      setSuccessMessage('');

      const headers = getAuthHeaders();
      const response = await fetch(`${backendUrl}/api/companies/${companyToDelete.id}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to delete company.');
        throw new Error(message);
      }

      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
      setSuccessMessage('Company deleted successfully.');
      await fetchCompanies();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to delete company.');
    }
  };

  // Handle view
  const handleViewClick = async (company) => {
    try {
      setErrorMessage('');
      setSuccessMessage('');

      if (!backendUrl) {
        throw new Error('Backend URL is not configured.');
      }

      const headers = getAuthHeaders();
      const [companyResponse, branchesResponse, usersResponse] = await Promise.all([
        fetch(`${backendUrl}/api/companies/${company.id}`, { headers }),
        fetch(`${backendUrl}/api/branches?company_id=${company.id}`, { headers }),
        fetch(`${backendUrl}/api/users?company_id=${company.id}`, { headers })
      ]);

      if (!companyResponse.ok) {
        const message = await getErrorMessage(companyResponse, 'Failed to load company details.');
        throw new Error(message);
      }

      const companyDetails = await companyResponse.json();
      const branches = branchesResponse.ok ? await branchesResponse.json() : [];
      const users = usersResponse.ok ? await usersResponse.json() : [];

      setViewCompany({
        ...companyDetails,
        branches: Array.isArray(branches) ? branches.length : 0,
        totalUsers: Array.isArray(users) ? users.length : 0,
        branchDetails: Array.isArray(branches) ? branches : [],
        status: typeof companyDetails.status === 'string' ? companyDetails.status.toLowerCase() : 'active'
      });
      setViewDialogOpen(true);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load company details.');
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = 
      company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-accent-blue" />
          <div>
            <h2 className="text-2xl font-bold text-text-dark font-manrope">Companies</h2>
            <p className="text-sm text-text-secondary font-ibm">Manage all companies and their branches</p>
          </div>
        </div>
        <Button
          data-testid="add-company-button"
          onClick={() => navigate('/settings/companies/add')}
          className="bg-accent-green hover:bg-accent-green/90 text-white font-ibm font-semibold h-11 px-6 rounded-lg"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Company
        </Button>
      </div>

      {(errorMessage || successMessage) && (
        <div className="space-y-3">
          {errorMessage && (
            <div
              data-testid="company-management-error"
              className="rounded-lg border border-accent-red/40 bg-accent-red/10 px-4 py-3 text-sm text-accent-red font-ibm"
            >
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div
              data-testid="company-management-success"
              className="rounded-lg border border-accent-green/40 bg-accent-green/10 px-4 py-3 text-sm text-accent-green font-ibm"
            >
              {successMessage}
            </div>
          )}
        </div>
      )}

      {/* Filters and Search Card */}
      <Card className="bg-primary-blue text-white rounded-xl shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <h3
              data-testid="companies-count"
              className="text-xl font-semibold font-manrope"
            >
              All Companies ({filteredCompanies.length})
            </h3>
            
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/60" />
                <Input
                  data-testid="company-search-input"
                  placeholder="Search companies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/40 font-ibm"
                />
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger 
                  data-testid="status-filter-select"
                  className="w-40 bg-white/10 border-white/20 text-white focus:bg-white/20 focus:border-white/40 font-ibm"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="status-filter-option-all" value="all">All Status</SelectItem>
                  <SelectItem data-testid="status-filter-option-active" value="active">Active</SelectItem>
                  <SelectItem data-testid="status-filter-option-inactive" value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              {/* Export Button */}
              <Button
                data-testid="export-companies-button"
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white font-ibm font-semibold"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Company Table */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div
            data-testid="companies-loading"
            className="p-12 text-center text-text-secondary font-ibm"
          >
            Loading companies...
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div
            data-testid="companies-empty"
            className="p-12 text-center text-text-secondary font-ibm"
          >
            No companies found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Company Code</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Company Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Phone</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">GST Number</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Modes</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Shipment Types</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Branches</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Total Users</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Status</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCompanies.map((company) => (
                  <tr
                    key={company.id}
                    data-testid={`company-row-${company.id}`}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      	<span
                        data-testid={`company-code-${company.id}`}
                        className="text-sm font-semibold text-accent-blue font-ibm"
                      	>
                        {company.code}
                      	</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-accent-blue/10 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-accent-blue" />
                        </div>
                        																			<span
                          data-testid={`company-name-${company.id}`}
                          className="text-sm font-medium text-text-dark font-ibm"
                        >
                          {company.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                        <span
                          data-testid={`company-email-${company.id}`}
                          className="text-sm text-text-secondary font-ibm"
                        >
                          {company.email}
                        </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        data-testid={`company-phone-${company.id}`}
                        className="text-sm text-text-secondary font-ibm"
                      >
                        {company.phone}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        data-testid={`company-gst-${company.id}`}
                        className="text-sm text-text-secondary font-mono"
                      >
                        {company.gst_number}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        data-testid={`company-modes-${company.id}`}
                        className="text-sm text-text-secondary font-ibm"
                      >
                        {(company.businessModes || []).join(', ') || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        data-testid={`company-shipment-types-${company.id}`}
                        className="text-sm text-text-secondary font-ibm"
                      >
                        {(company.shipmentTypes || []).join(', ') || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-accent-cyan/10 rounded-full">
                        <span
                          data-testid={`company-branches-${company.id}`}
                          className="text-sm font-bold text-accent-cyan font-ibm"
                        >
                          {company.branches}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-accent-purple/10 rounded-full">
                        <span
                          data-testid={`company-users-${company.id}`}
                          className="text-sm font-bold text-accent-purple font-ibm"
                        >
                          {company.totalUsers}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Badge
                        data-testid={`company-status-${company.id}`}
                        className={`
                          font-ibm font-semibold border-0
                          ${company.status === 'active' ? 'bg-accent-green/15 text-accent-green' : 'bg-gray-200 text-gray-600'}
                        `}
                      >
                        {company.status === 'active' ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          data-testid={`view-company-${company.id}`}
                          onClick={() => handleViewClick(company)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-accent-blue/10 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 text-accent-blue hover:text-accent-blue/80" />
                        </button>
                        <button
                          data-testid={`edit-company-${company.id}`}
                          onClick={() => navigate(`/settings/companies/edit/${company.id}`)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-accent-orange/10 rounded-lg transition-colors"
                          title="Edit Company"
                        >
                          <Edit2 className="w-4 h-4 text-accent-orange hover:text-accent-orange/80" />
                        </button>
                        <button
                          data-testid={`delete-company-${company.id}`}
                          onClick={() => handleDeleteClick(company)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-accent-red/10 rounded-lg transition-colors"
                          title="Delete Company"
                        >
                          <Trash2 className="w-4 h-4 text-accent-red hover:text-accent-red/80" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle
              data-testid="delete-company-title"
              className="flex items-center gap-2 text-text-dark font-manrope"
            >
              <AlertCircle className="w-5 h-5 text-accent-red" />
              Delete Company
            </AlertDialogTitle>
            <AlertDialogDescription
              data-testid="delete-company-description"
              className="text-text-secondary font-ibm"
            >
              Are you sure you want to delete <span className="font-semibold text-text-dark">{companyToDelete?.name}</span>? 
              This action cannot be undone and will also delete all associated users and branches.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-company-cancel" className="font-ibm">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              data-testid="delete-company-confirm"
              onClick={handleDeleteConfirm}
              className="bg-accent-red hover:bg-accent-red/90 font-ibm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Company Dialog */}
      <AlertDialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <AlertDialogContent className="bg-white max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle
              data-testid="company-details-title"
              className="flex items-center gap-2 text-text-dark font-manrope text-2xl"
            >
              <Building2 className="w-6 h-6 text-accent-blue" />
              Company Details
            </AlertDialogTitle>
          </AlertDialogHeader>
          
          {viewCompany && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Company Name</p>
                  <p
                    data-testid="company-details-name"
                    className="text-base font-semibold text-text-dark font-ibm"
                  >
                    {viewCompany.name}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Company Code</p>
                  <p
                    data-testid="company-details-code"
                    className="text-base font-semibold text-accent-blue font-ibm"
                  >
                    {viewCompany.code}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Email</p>
                  <p
                    data-testid="company-details-email"
                    className="text-base text-text-dark font-ibm"
                  >
                    {viewCompany.email}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Phone</p>
                  <p
                    data-testid="company-details-phone"
                    className="text-base text-text-dark font-ibm"
                  >
                    {viewCompany.phone}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Secondary Phone</p>
                  <p
                    data-testid="company-details-secondary-phone"
                    className="text-base text-text-dark font-ibm"
                  >
                    {viewCompany.phone_secondary || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">GST Number</p>
                  <p
                    data-testid="company-details-gst"
                    className="text-base text-text-dark font-mono"
                  >
                    {viewCompany.gst_number}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">PAN Number</p>
                  <p
                    data-testid="company-details-pan"
                    className="text-base text-text-dark font-mono"
                  >
                    {viewCompany.pan_number || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">CIN Number</p>
                  <p
                    data-testid="company-details-cin"
                    className="text-base text-text-dark font-mono"
                  >
                    {viewCompany.cin_number || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Status</p>
                  <Badge
                    data-testid="company-details-status"
                    className={`${viewCompany.status === 'active' ? 'bg-accent-green/15 text-accent-green' : 'bg-gray-200 text-gray-600'} font-ibm`}
                  >
                    {viewCompany.status}
                  </Badge>
                </div>
                <div className="col-span-2">
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Address</p>
                  <p
                    data-testid="company-details-address"
                    className="text-base text-text-dark font-ibm"
                  >
                    {viewCompany.address}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Branches</p>
                  {viewCompany.branchDetails?.length > 0 ? (
                    <div className="space-y-2">
                      {viewCompany.branchDetails.map((branch) => (
                        <div
                          key={branch.id}
                          data-testid={`company-details-branch-${branch.id}`}
                          className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                        >
                          <p
                            data-testid={`company-details-branch-name-${branch.id}`}
                            className="text-sm font-semibold text-text-dark font-ibm"
                          >
                            {branch.nick_name}
                          </p>
                          <p
                            data-testid={`company-details-branch-location-${branch.id}`}
                            className="text-xs text-text-secondary font-ibm"
                          >
                            {[branch.city, branch.state].filter(Boolean).join(', ')}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p
                      data-testid="company-details-no-branches"
                      className="text-sm text-text-secondary font-ibm"
                    >
                      No branches found.
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Total Branches</p>
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-accent-cyan/10 rounded-full">
                    <span
                      data-testid="company-details-branches"
                      className="text-base font-bold text-accent-cyan font-ibm"
                    >
                      {viewCompany.branches}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Total Users</p>
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-accent-purple/10 rounded-full">
                    <span
                      data-testid="company-details-users"
                      className="text-base font-bold text-accent-purple font-ibm"
                    >
                      {viewCompany.totalUsers}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="company-details-close" className="font-ibm">Close</AlertDialogCancel>
            <Button
              data-testid="company-details-edit"
              onClick={() => {
                setViewDialogOpen(false);
                navigate(`/settings/companies/edit/${viewCompany.id}`);
              }}
              className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Company
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanyManagement;
